import { createModel } from "@rematch/core";
import { api } from "api";
import {
  ChangeFailureStatusRuleDto,
  CreateFailureStatusCommand,
  FailureStatusRulesResponseVm,
  FailureStatusVm,
} from "api/generated/lumen";
import { RootModel } from "app/store";
import { mapToGetAllQuery } from "utils";
import {
  ListRequestParams,
  OrderByType,
  mergeListParams,
  mergeListParamsWithPagination,
} from "widgets/table/useTableUtils";

interface StatusesState {
  listParams: ListRequestParams;
  list: FailureStatusVm[];

  editingItemId?: string;

  statusRules?: FailureStatusRulesResponseVm;
  isSaveDisabled?: boolean;
}

const initialState: StatusesState = {
  listParams: {
    pageSize: 10,
    order: [
      {
        orderBy: "name",
        orderByType: OrderByType.Ascending,
      },
    ],
  },
  list: [],

  editingItemId: undefined,

  statusRules: undefined,
  isSaveDisabled: true,
};

export const statuses = createModel<RootModel>()({
  state: initialState,
  reducers: {
    resetListParams(state) {
      state.listParams = initialState.listParams;
    },
    setList(
      state,
      {
        data,
        listParams,
      }: { data: FailureStatusVm[]; listParams: ListRequestParams }
    ) {
      state.list = data;
      state.listParams = listParams;
    },

    setEditingItemId(state, data?: string) {
      state.editingItemId = data;
    },
    resetEditingItemData(state) {
      state.editingItemId = undefined;
    },

    setStatusRules(state, data: FailureStatusRulesResponseVm) {
      state.statusRules = data;
    },
    resetStatusRules(state) {
      state.statusRules = initialState.statusRules;
    },
    setIsSaveDisabled(state, payload: boolean) {
      state.isSaveDisabled = payload;
    },

    reset: () => initialState,
  },
  effects: (dispatch) => {
    const { statuses } = dispatch;

    return {
      async fetchList(params: ListRequestParams = {}, state): Promise<void> {
        const mergedParams = mergeListParams(state.statuses.listParams, params);

        const data =
          await api.lumen.failures.apiFailuresGetFailureStatuesesPost({
            getAllFailureStatusesQuery: mapToGetAllQuery(mergedParams),
          });

        const { items, ...pagination } = data;

        dispatch(
          statuses.setList({
            data: items || [],
            listParams: mergeListParamsWithPagination(mergedParams, pagination),
          })
        );
      },

      async createStatus(values: CreateFailureStatusCommand): Promise<void> {
        await api.lumen.failureStatus.apiFailureStatusFailureSatusPost({
          createFailureStatusCommand: values,
        });
      },

      async editStatus({ id, values }): Promise<void> {
        await api.lumen.failureStatus.apiFailureStatusIdPut({
          id,
          failureStatusDto: values,
        });
      },

      async deleteStatus(id: string): Promise<void> {
        await api.lumen.failureStatus.apiFailureStatusIdDelete({
          id,
        });
      },

      async fetchStatusRules(): Promise<void> {
        const data =
          await api.lumen.failureStatus.apiFailureStatusGetAllRulesPost();

        dispatch(statuses.setStatusRules(data));
      },

      async updateStatusRules(rules: Array<ChangeFailureStatusRuleDto>) {
        await api.lumen.failureStatus.apiFailureStatusChangeRulesPut({
          changeFailureStatusRulesCommand: {
            rules,
          },
        });
      },
    };
  },
});
